import { jsx as _jsx } from "react/jsx-runtime";
import { CATEGORY, affixesFor, baseGearType, basesForSlot, gearRarity, ids, maxLevel, maxQuality, playerSpellIds, slotFamily, treeGraph, treeGrid, TREE_KEYS, } from "@cte2/schema";
import { createContext, useContext, useMemo } from "react";
const SnapshotContext = createContext(null);
export function SnapshotProvider({ snapshot, path, assets, children, }) {
    const world = useMemo(() => {
        const gridCache = new Map();
        const graphCache = new Map();
        const baseCache = new Map();
        const affixCache = new Map();
        const iconCache = new Map();
        const gearIconCache = new Map();
        const itemIconCache = new Map();
        // Named rather than reached through the world object, because `gearIcon` calls it and a
        // destructured `const { gearIcon } = world` would lose the `this` that needed.
        const itemIcon = (itemId) => {
            let found = itemIconCache.get(itemId);
            if (found === undefined) {
                found = assets.itemIconUrl(itemId);
                itemIconCache.set(itemId, found);
            }
            return found;
        };
        const rarities = ids(snapshot, CATEGORY.gearRarity)
            .map((id) => gearRarity(snapshot, id))
            .filter((r) => r !== undefined)
            // The ladder a player reads, which `item_tier` almost gives on its own: common 0 through
            // legendary 4, then runeword away at 10. What it does not settle is mythic against unique
            // — the pack gives both tier 5 — so they fell out in whatever order the registry listed
            // them, which is alphabetical, which put mythic first. Unique is the rung above the
            // ordinary ladder and below the mythic cap in every list the game shows, so a tie is
            // broken in its favour and the dropdown reads common → mythic the way the game does.
            .sort((a, b) => a.itemTier - b.itemTier || Number(b.isUniqueItem) - Number(a.isUniqueItem));
        const rarityById = new Map(rarities.map((r) => [r.id, r]));
        const slots = ids(snapshot, CATEGORY.gearSlot)
            .map((id) => ({ id, family: slotFamily(snapshot, id) ?? "Other", name: id }))
            .sort((a, b) => a.family.localeCompare(b.family) || a.id.localeCompare(b.id));
        return {
            snapshot,
            path,
            maxLevel: maxLevel(snapshot),
            maxQuality: maxQuality(snapshot),
            rarities,
            slots,
            spellIds: playerSpellIds(snapshot),
            supportGemIds: ids(snapshot, CATEGORY.supportGem).sort(),
            auraIds: ids(snapshot, CATEGORY.aura).sort(),
            statBuffIds: ids(snapshot, CATEGORY.statBuff).sort(),
            exileEffectIds: ids(snapshot, CATEGORY.exileEffect).sort(),
            gemIds: ids(snapshot, CATEGORY.gem).sort(),
            runeIds: ids(snapshot, CATEGORY.rune).sort(),
            runewordIds: ids(snapshot, CATEGORY.runeword).sort(),
            schoolIds: ids(snapshot, CATEGORY.spellSchool).sort(),
            grid(tree) {
                const key = TREE_KEYS[tree];
                if (!gridCache.has(key))
                    gridCache.set(key, treeGrid(snapshot, key));
                return gridCache.get(key);
            },
            graph(tree) {
                const key = TREE_KEYS[tree];
                if (!graphCache.has(key))
                    graphCache.set(key, treeGraph(snapshot, key));
                return graphCache.get(key);
            },
            basesFor(slotId) {
                let found = baseCache.get(slotId);
                if (!found) {
                    found = basesForSlot(snapshot, slotId).sort((a, b) => a.id.localeCompare(b.id));
                    baseCache.set(slotId, found);
                }
                return found;
            },
            affixPool(baseId, type) {
                const key = `${baseId} ${type}`;
                let found = affixCache.get(key);
                if (!found) {
                    found = affixesFor(snapshot, baseId, type).sort((a, b) => a.id.localeCompare(b.id));
                    affixCache.set(key, found);
                }
                return found;
            },
            rarity: (id) => rarityById.get(id),
            gearIcon(baseId) {
                let found = gearIconCache.get(baseId);
                if (found === undefined) {
                    found = null;
                    for (const itemId of baseGearType(snapshot, baseId)?.itemIds ?? []) {
                        const url = itemIcon(itemId);
                        if (url !== null) {
                            found = url;
                            break;
                        }
                    }
                    gearIconCache.set(baseId, found);
                }
                return found;
            },
            itemIcon,
            icon(resourcePath) {
                if (resourcePath === undefined || resourcePath.length === 0)
                    return null;
                if (!iconCache.has(resourcePath)) {
                    iconCache.set(resourcePath, assets.assetUrl(resourcePath));
                }
                return iconCache.get(resourcePath) ?? null;
            },
        };
    }, [snapshot, path, assets]);
    return _jsx(SnapshotContext.Provider, { value: world, children: children });
}
export function useWorld() {
    const world = useContext(SnapshotContext);
    if (world === null)
        throw new Error("useWorld outside a SnapshotProvider");
    return world;
}
//# sourceMappingURL=world.js.map